import { Box } from '@mantine/core';
import { useEffect, useMemo, useState } from 'react';
import { useRevisionLockProvider } from 'components/forms/revision/fill-out/data/RevisionLockProvider';

/**
 * Formats a date-time string for compact debug display.
 */
function formatTimestamp(value: string | null) {
  if (!value) {
    return '-';
  }

  const date = new Date(value);

  if (Number.isNaN(date.getTime())) {
    return value;
  }

  return date.toLocaleTimeString();
}

/**
 * Formats a countdown to a target time in seconds.
 */
function formatCountdown(targetAt: number | null, now: number) {
  if (!targetAt) {
    return '-';
  }

  const secondsLeft = Math.max(0, Math.ceil((targetAt - now) / 1000));

  return `${secondsLeft}s`;
}

/**
 * Revision lock diagnostics overlay for local debugging.
 */
export default function RevisionLockDebugPanel() {
  const { debugInfo } = useRevisionLockProvider();
  const [now, setNow] = useState(Date.now());
  const [collapsed, setCollapsed] = useState(false);

  useEffect(() => {
    const interval = setInterval(() => {
      setNow(Date.now());
    }, 1000);

    return () => clearInterval(interval);
  }, []);

  const ttlCountdown = useMemo(() => {
    if (!debugInfo.expiresAt) {
      return '-';
    }

    return formatCountdown(new Date(debugInfo.expiresAt).getTime(), now);
  }, [debugInfo.expiresAt, now]);

  const rows = useMemo(
    () => [
      ['revisionId', String(debugInfo.revisionId)],
      ['stage', debugInfo.stage],
      ['lockState', debugInfo.lockState ?? '-'],
      ['takeoverState', debugInfo.takeoverState ?? '-'],
      ['pollingMode', debugInfo.pollingMode],
      ['isLocked', String(debugInfo.isLocked)],
      ['lockedByOther', String(debugInfo.lockedByOther)],
      ['isLockedByCurrentUser', String(debugInfo.isLockedByCurrentUser)],
      ['isLockedByCurrentSession', String(debugInfo.isLockedByCurrentSession)],
      ['lockToken', debugInfo.lockToken],
      ['lockedBy', debugInfo.lockedByUserName ?? '-'],
      ['nextHeartbeatIn', formatCountdown(debugInfo.nextHeartbeatAt, now)],
      ['nextTakeoverPollIn', formatCountdown(debugInfo.nextTakeoverPollAt, now)],
      ['ttlRemaining', ttlCountdown],
      ['heartbeatInFlight', String(debugInfo.heartbeatInFlight)],
      ['takeoverWaitInFlight', String(debugInfo.takeoverWaitInFlight)],
      ['lockedAt', formatTimestamp(debugInfo.lockedAt)],
      ['lastActivityAt', formatTimestamp(debugInfo.lastActivityAt)],
      ['expiresAt', formatTimestamp(debugInfo.expiresAt)],
      ['takeoverRequestedAt', formatTimestamp(debugInfo.takeoverRequestedAt)],
    ],
    [debugInfo, now, ttlCountdown]
  );

  const collapsedSummary = useMemo(
    () => `nextHeartbeatIn: ${formatCountdown(debugInfo.nextHeartbeatAt, now)} | ttlRemaining: ${ttlCountdown}`,
    [debugInfo.nextHeartbeatAt, now, ttlCountdown]
  );

  return (
    <Box
      pos="absolute"
      top={16}
      right={16}
      px={12}
      py={10}
      maw={360}
      onClick={() => setCollapsed((value) => !value)}
      sx={(theme) => ({
        zIndex: 20,
        cursor: 'pointer',
        pointerEvents: 'auto',
        userSelect: 'none',
        borderRadius: 8,
        border: `1px solid ${theme.colors.gray[4]}`,
        background: 'rgba(255, 255, 255, 0.96)',
        boxShadow: theme.shadows.sm,
        fontFamily: 'monospace',
        fontSize: 12,
        lineHeight: 1.45,
        whiteSpace: 'pre-wrap',
      })}
    >
      <Box mb={collapsed ? 0 : 6}>
        <strong>lock debug</strong> [{collapsed ? 'expand' : 'collapse'}]
      </Box>
      {collapsed ? (
        <Box>{collapsedSummary}</Box>
      ) : (
        rows.map(([label, value]) => (
          <Box key={label}>
            <strong>{label}</strong>: {value}
          </Box>
        ))
      )}
    </Box>
  );
}
